import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import PageHeader from '../components/PageHeader'
import TableFilters from '../components/TableFilters'

const CATEGORIES = ['Salary', 'Fixed', 'External Help', 'Variable']
const EXTERNAL_SUBTYPES = ['Developer', 'Designer', 'Marketing', 'Content Writer', 'Other']

const EMPTY = {
  category: 'Variable',
  sub_type: '',
  description: '',
  amount: '',
  month: '',
  recurring: false,
  paid: false,
  due_date: '',
  project_ref: '',
  notes: '',
}

function fmt(n) { return '₹' + Number(n).toLocaleString('en-IN') }

function catBadge(c) {
  const map = {
    Salary: 'badge badge-blue',
    Fixed: 'badge badge-purple',
    'External Help': 'badge badge-amber',
    Variable: 'badge badge-gray',
  }
  return map[c] || 'badge badge-gray'
}

function subBadge(s) {
  const map = {
    Developer: 'badge badge-blue',
    Designer: 'badge badge-purple',
    Marketing: 'badge badge-amber',
    'Content Writer': 'badge badge-green',
    Other: 'badge badge-gray',
  }
  return map[s] || 'badge badge-gray'
}

function getCurrentMonth() { return new Date().toISOString().slice(0, 7) }

function prevMonth(ym) {
  const [y, m] = ym.split('-').map(Number)
  const d = new Date(y, m - 2, 1)
  return d.toISOString().slice(0, 7)
}

function shiftDueDate(dateStr, toMonth) {
  if (!dateStr) return ''
  const day = dateStr.slice(8, 10)
  return toMonth + '-' + day
}

export default function Expenses() {
  const [expenses, setExpenses] = useState([])
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [filtered, setFiltered] = useState([])
  const [viewMonth, setViewMonth] = useState(getCurrentMonth())
  const [rolling, setRolling] = useState(false)

  const FILTERS = [
    { key: 'search', label: 'Search', type: 'text', fields: ['description', 'notes', 'project_ref'] },
    { key: 'category', label: 'Category', type: 'select', options: CATEGORIES },
  ]
  const handleFiltered = useCallback(d => setFiltered(d), [])

  useEffect(() => {
    supabase.from('expenses').select('*').order('due_date', { ascending: true }).then(({ data }) => {
      if (data) setExpenses(data)
    })
  }, [])

  // Month-filtered slices
  const monthExpenses = filtered.filter(e => e.month === viewMonth)
  const salaryRows   = monthExpenses.filter(e => e.category === 'Salary')
  const fixedRows    = monthExpenses.filter(e => e.category === 'Fixed')
  const externalRows = monthExpenses.filter(e => e.category === 'External Help')
  const variableRows = monthExpenses.filter(e => e.category === 'Variable')

  const salaryTotal   = salaryRows.reduce((s, e) => s + (Number(e.amount) || 0), 0)
  const fixedTotal    = fixedRows.reduce((s, e) => s + (Number(e.amount) || 0), 0)
  const externalTotal = externalRows.reduce((s, e) => s + (Number(e.amount) || 0), 0)
  const variableTotal = variableRows.reduce((s, e) => s + (Number(e.amount) || 0), 0)
  const grandTotal    = salaryTotal + fixedTotal + externalTotal + variableTotal

  const today = new Date().toISOString().slice(0, 10)
  const unpaidTotal = monthExpenses.filter(e => !e.paid).reduce((s, e) => s + (Number(e.amount) || 0), 0)
  const unpaidCount = monthExpenses.filter(e => !e.paid).length

  // Roll-over detection: no recurring rows in viewMonth but there are in prevMonth
  const hasRecurringThisMonth = monthExpenses.some(e => e.recurring)
  const prev = prevMonth(viewMonth)
  const prevRecurring = filtered.filter(e => e.month === prev && e.recurring)
  const showRolloverBanner = !hasRecurringThisMonth && prevRecurring.length > 0

  function openAdd() { setForm({ ...EMPTY, month: viewMonth }); setEditing(null); setModal(true) }
  function openEdit(e) { setForm(e); setEditing(e.id); setModal(true) }

  async function save() {
    setSaving(true)
    const payload = { ...form, amount: Number(form.amount) || 0 }
    if (editing) {
      const { data, error } = await supabase.from('expenses').update(payload).eq('id', editing).select()
      if (error) { alert('Save failed: ' + error.message); setSaving(false); return }
      setExpenses(prev => prev.map(e => e.id === editing ? data[0] : e))
    } else {
      const { data, error } = await supabase.from('expenses').insert([payload]).select()
      if (error) { alert('Save failed: ' + error.message); setSaving(false); return }
      setExpenses(prev => [...prev, data[0]])
    }
    setSaving(false)
    setModal(false)
  }

  async function togglePaid(exp) {
    const newPaid = !exp.paid
    await supabase.from('expenses').update({ paid: newPaid }).eq('id', exp.id)
    setExpenses(prev => prev.map(e => e.id === exp.id ? { ...e, paid: newPaid } : e))
  }

  async function remove(id) {
    if (!window.confirm('Delete this expense?')) return
    await supabase.from('expenses').delete().eq('id', id)
    setExpenses(prev => prev.filter(e => e.id !== id))
  }

  async function rollover() {
    setRolling(true)
    const toInsert = prevRecurring.map(({ id: _id, created_at: _c, ...rest }) => ({
      ...rest,
      month: viewMonth,
      due_date: shiftDueDate(rest.due_date, viewMonth),
      paid: false,
    }))
    const { data } = await supabase.from('expenses').insert(toInsert).select()
    if (data) setExpenses(prev => [...prev, ...data])
    setRolling(false)
  }

  const monthLabel = new Date(viewMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <PageHeader
        title="Expenses"
        subtitle={monthLabel}
        action={<button className="btn btn-primary" onClick={openAdd}>+ Add expense</button>}
      />
      <div style={{ padding: 28, flex: 1 }}>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Salary', value: salaryTotal, color: 'var(--accent)', count: salaryRows.length },
            { label: 'Fixed', value: fixedTotal, color: 'var(--purple)', count: fixedRows.length },
            { label: 'External Help', value: externalTotal, color: 'var(--warning)', count: externalRows.length },
            { label: 'Variable', value: variableTotal, color: 'var(--text2)', count: variableRows.length },
            { label: 'Unpaid', value: unpaidTotal, color: 'var(--danger)', count: unpaidCount, sub: 'items unpaid' },
          ].map(({ label, value, color, count, sub }) => (
            <div key={label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 18px', boxShadow: 'var(--shadow)' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 18, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: value > 0 ? color : 'var(--text3)' }}>{fmt(value)}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{count} {sub || 'this month'}</div>
            </div>
          ))}
        </div>

        {/* Breakdown bar */}
        {grandTotal > 0 && (
          <div className="card" style={{ padding: '12px 18px', marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>Breakdown — {monthLabel}</div>
            <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', gap: 2, marginBottom: 8 }}>
              {salaryTotal > 0 && <div style={{ flex: salaryTotal, background: '#1B4FD8', minWidth: 4 }} title={`Salary: ${fmt(salaryTotal)}`} />}
              {fixedTotal > 0 && <div style={{ flex: fixedTotal, background: '#7C3AED', minWidth: 4 }} title={`Fixed: ${fmt(fixedTotal)}`} />}
              {externalTotal > 0 && <div style={{ flex: externalTotal, background: '#D97706', minWidth: 4 }} title={`External: ${fmt(externalTotal)}`} />}
              {variableTotal > 0 && <div style={{ flex: variableTotal, background: '#6B7280', minWidth: 4 }} title={`Variable: ${fmt(variableTotal)}`} />}
            </div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12 }}>
              {[
                { label: 'Salary', val: salaryTotal, bg: '#1B4FD8' },
                { label: 'Fixed', val: fixedTotal, bg: '#7C3AED' },
                { label: 'External', val: externalTotal, bg: '#D97706' },
                { label: 'Variable', val: variableTotal, bg: '#6B7280' },
              ].filter(x => x.val > 0).map(x => (
                <div key={x.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: x.bg, flexShrink: 0 }} />
                  <span style={{ color: 'var(--text2)' }}>{x.label}: <strong>{fmt(x.val)}</strong> ({Math.round(x.val / grandTotal * 100)}%)</span>
                </div>
              ))}
              <div style={{ marginLeft: 'auto', fontWeight: 600, color: 'var(--text)' }}>Total: {fmt(grandTotal)}</div>
            </div>
          </div>
        )}

        {/* Month selector */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <input type="month" value={viewMonth} onChange={e => setViewMonth(e.target.value)} className="filter-input" style={{ width: 160 }} />
          <TableFilters filters={FILTERS} data={expenses} onFiltered={handleFiltered} />
        </div>

        {/* Roll-over banner */}
        {showRolloverBanner && (
          <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 8, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: 'var(--warning)', fontSize: 18 }}>⚠</span>
            <span style={{ fontSize: 13, color: 'var(--text)', flex: 1 }}>
              No recurring expenses for <strong>{monthLabel}</strong>. Roll over {prevRecurring.length} recurring items (salary + fixed) from the previous month?
            </span>
            <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: 12 }} onClick={rollover} disabled={rolling}>
              {rolling ? 'Rolling over…' : `Roll over ${prevRecurring.length} items`}
            </button>
          </div>
        )}

        {/* SALARY section */}
        <SectionTable
          title="Salary"
          rows={salaryRows}
          total={salaryTotal}
          columns={['Person', 'Amount', 'Due date', 'Paid', 'Notes', '']}
          renderRow={e => (
            <tr key={e.id}>
              <td style={{ fontWeight: 500 }}>
                {e.description}
                {e.recurring && <span className="badge badge-gray" style={{ marginLeft: 6, fontSize: 10 }}>Recurring</span>}
              </td>
              <td style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(e.amount)}</td>
              <td style={{ color: 'var(--text2)' }}>{e.due_date || '—'}</td>
              <PaidCell e={e} togglePaid={togglePaid} />
              <td style={{ color: 'var(--text2)', fontSize: 12 }}>{e.notes || '—'}</td>
              <ActionCell e={e} openEdit={openEdit} remove={remove} />
            </tr>
          )}
          empty="No salary entries this month — add one or roll over from previous month"
        />

        {/* FIXED section */}
        <SectionTable
          title="Fixed"
          rows={fixedRows}
          total={fixedTotal}
          columns={['Description', 'Amount', 'Due date', 'Paid', 'Notes', '']}
          renderRow={e => (
            <tr key={e.id}>
              <td style={{ fontWeight: 500 }}>
                {e.description}
                {e.recurring && <span className="badge badge-gray" style={{ marginLeft: 6, fontSize: 10 }}>Recurring</span>}
              </td>
              <td style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(e.amount)}</td>
              <td style={{ color: 'var(--text2)' }}>{e.due_date || '—'}</td>
              <PaidCell e={e} togglePaid={togglePaid} />
              <td style={{ color: 'var(--text2)', fontSize: 12 }}>{e.notes || '—'}</td>
              <ActionCell e={e} openEdit={openEdit} remove={remove} />
            </tr>
          )}
          empty="No fixed expenses this month"
        />

        {/* EXTERNAL HELP section */}
        <SectionTable
          title="External Help"
          rows={externalRows}
          total={externalTotal}
          columns={['Type', 'Person / Vendor', 'Project', 'Amount', 'Paid', 'Notes', '']}
          renderRow={e => (
            <tr key={e.id}>
              <td>{e.sub_type ? <span className={subBadge(e.sub_type)}>{e.sub_type}</span> : '—'}</td>
              <td style={{ fontWeight: 500 }}>{e.description}</td>
              <td style={{ color: 'var(--text2)', fontSize: 12 }}>{e.project_ref || '—'}</td>
              <td style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(e.amount)}</td>
              <PaidCell e={e} togglePaid={togglePaid} />
              <td style={{ color: 'var(--text2)', fontSize: 12 }}>{e.notes || '—'}</td>
              <ActionCell e={e} openEdit={openEdit} remove={remove} />
            </tr>
          )}
          empty="No external help this month"
        />

        {/* VARIABLE section */}
        <SectionTable
          title="Variable"
          rows={variableRows}
          total={variableTotal}
          columns={['Description', 'Amount', 'Due date', 'Paid', 'Notes', '']}
          renderRow={e => (
            <tr key={e.id}>
              <td style={{ fontWeight: 500 }}>{e.description}</td>
              <td style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(e.amount)}</td>
              <td style={{ color: 'var(--text2)' }}>{e.due_date || '—'}</td>
              <PaidCell e={e} togglePaid={togglePaid} />
              <td style={{ color: 'var(--text2)', fontSize: 12 }}>{e.notes || '—'}</td>
              <ActionCell e={e} openEdit={openEdit} remove={remove} />
            </tr>
          )}
          empty="No variable expenses this month"
        />

      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3>{editing ? 'Edit expense' : 'Add expense'}</h3>
              <button className="modal-close" onClick={() => setModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-field"><label>Category</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value, sub_type: '' })}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                {form.category === 'External Help' && (
                  <div className="form-field"><label>Type</label>
                    <select value={form.sub_type} onChange={e => setForm({ ...form, sub_type: e.target.value })}>
                      <option value="">Select type…</option>
                      {EXTERNAL_SUBTYPES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                )}
                <div className="form-field"><label>Amount (₹) *</label>
                  <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
                </div>
              </div>

              <div className="form-field">
                <label>{form.category === 'Salary' ? 'Person name & role *' : form.category === 'External Help' ? 'Person / Vendor name *' : 'Description *'}</label>
                <input
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  placeholder={form.category === 'Salary' ? 'e.g. Ankita Kundu — SEO Executive' : form.category === 'External Help' ? 'e.g. Rahul Sharma' : 'e.g. Office rent, Google Ads'}
                />
              </div>

              {form.category === 'External Help' && (
                <div className="form-field"><label>Project (optional)</label>
                  <input value={form.project_ref} onChange={e => setForm({ ...form, project_ref: e.target.value })} placeholder="e.g. Aryanss IT — New Website" />
                </div>
              )}

              <div className="form-row">
                <div className="form-field"><label>Month</label>
                  <input type="month" value={form.month} onChange={e => setForm({ ...form, month: e.target.value })} />
                </div>
                <div className="form-field"><label>Due date</label>
                  <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
                </div>
              </div>

              <div className="form-row">
                {(form.category === 'Salary' || form.category === 'Fixed') && (
                  <div className="form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <input type="checkbox" checked={form.recurring} onChange={e => setForm({ ...form, recurring: e.target.checked })} style={{ width: 16, height: 16 }} />
                    <label style={{ margin: 0 }}>Recurring monthly</label>
                  </div>
                )}
                <div className="form-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={form.paid} onChange={e => setForm({ ...form, paid: e.target.checked })} style={{ width: 16, height: 16 }} />
                  <label style={{ margin: 0 }}>Paid</label>
                </div>
              </div>

              <div className="form-field"><label>Notes</label>
                <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ resize: 'vertical' }} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.description || !form.amount}>
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add expense'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function SectionTable({ title, rows, total, columns, renderRow, empty }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{title} ({rows.length})</div>
        {rows.length > 0 && <div style={{ fontSize: 12, color: 'var(--text3)' }}>{fmt(total)}</div>}
      </div>
      <div className="card" style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>{columns.map((c, i) => <th key={i}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map(renderRow)}
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} style={{ textAlign: 'center', color: 'var(--text3)', padding: 28 }}>{empty}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PaidCell({ e, togglePaid }) {
  return (
    <td>
      <button
        className={e.paid ? 'badge badge-green' : 'badge badge-amber'}
        onClick={() => togglePaid(e)}
        style={{ cursor: 'pointer', border: 'none' }}
      >
        {e.paid ? 'Paid' : 'Unpaid'}
      </button>
    </td>
  )
}

function ActionCell({ e, openEdit, remove }) {
  return (
    <td>
      <div style={{ display: 'flex', gap: 4 }}>
        <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => openEdit(e)}>Edit</button>
        <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => remove(e.id)}>Del</button>
      </div>
    </td>
  )
}
